import React, { Component } from 'react';
import moment from 'moment';
import { NativeDateType, RangeState } from '@philips-internal/oswald-ui-constants';
import { AbortableNavigateEvent, ChangeEvent, NavigateEvent } from '@philips-internal/oswald-ui-core/events';
import { getPrettyDateValue } from '@philips-internal/oswald-ui-model/lib/utils/index.js';
import { createAbort, getLostProps, isFunction, isMobile, shallowCompare } from '@philips-internal/oswald-ui-utils';
import { NativeDate } from '../native-date/native-date.js';
import { DatePickerBasic } from './basic/date-picker-basic.js';
import { DatePickerMode } from './date-picker-constants.js';
import { DatePickerProperties } from './date-picker-types.js';
import { decrementMode, getTypeOfNavigabilityMode, incrementMode, optimizeRanges } from './date-picker-util.js';
import { DatePickerInterval } from './interval/date-picker-interval.js';
import './style/date-picker.css';
class DatePicker extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            date: this.props.initialDate ?? DatePickerProperties.initialDate.defaultValue,
            innerMode: this.props.mode ?? DatePickerProperties.mode.defaultValue,
            intervalInnerValue: [],
            isExpandedWeekMonthly: this.props.mode !== DatePickerMode.WEEK,
        };
        this.onCellClick = (cellValue) => {
            const { enabled = DatePickerProperties.enabled.defaultValue, loading = DatePickerProperties.loading.defaultValue, mode = DatePickerProperties.mode.defaultValue, onAfterNavigate, onNavigate, showExpander = DatePickerProperties.showExpander.defaultValue, value, } = this.props;
            const { date, innerMode, oldDate } = this.state;
            if (!enabled || loading) {
                return;
            }
            const cellValueMoment = moment(cellValue);
            const valueMoment = moment(value);
            const isSameDate = this.isSameDate(cellValueMoment, valueMoment);
            const isSameMonthAndYear = this.isSameMonthAndYear(cellValueMoment, valueMoment);
            const decrementModes = [DatePickerMode.DECADES, DatePickerMode.YEARLY];
            const isDecadeOrYearly = decrementModes.includes(innerMode);
            const finalValue = isSameDate ? undefined : cellValue;
            const newDate = moment(finalValue).utc();
            const newInnerMode = isDecadeOrYearly
                ? decrementMode(innerMode)
                : innerMode;
            const isNewInnerModeDecadeOrYearly = decrementModes.includes(newInnerMode);
            const shouldSkipNavigateEvents = isSameDate || newInnerMode === DatePickerMode.MONTHLY && isSameMonthAndYear || isNewInnerModeDecadeOrYearly;
            if (shouldSkipNavigateEvents) {
                return this.setState({
                    date: newDate,
                    innerMode: newInnerMode,
                    oldDate: date,
                }, () => {
                    if (innerMode === mode || showExpander) {
                        this.onApply({ value: finalValue });
                    }
                });
            }
            const currentDate = moment(oldDate).utc();
            const abort = createAbort();
            const period = getTypeOfNavigabilityMode(newInnerMode);
            const dates = this.getDates(currentDate, newDate, period);
            if (isFunction(onNavigate)) {
                onNavigate(new AbortableNavigateEvent({ abort, from: dates.initialDate, to: dates.finalDate }));
            }
            void abort.promise.then(aborted => {
                if (aborted) {
                    return;
                }
                this.setState({
                    date: newDate,
                    innerMode: newInnerMode,
                    oldDate: date,
                }, () => {
                    if (isFunction(onAfterNavigate)) {
                        onAfterNavigate(new NavigateEvent({ from: dates.initialDate, to: dates.finalDate }));
                    }
                    if (innerMode === mode || showExpander) {
                        this.onApply({ value: finalValue });
                    }
                });
            });
        };
        this.onIntervalCellClick = (selectedValue) => {
            const { enabled = DatePickerProperties.enabled.defaultValue, loading = DatePickerProperties.loading.defaultValue, } = this.props;
            if (!enabled || loading) {
                return;
            }
            const [startDate, endDate] = this.state.intervalInnerValue;
            let intervalInnerValue;
            if (!startDate && !endDate) {
                intervalInnerValue = [selectedValue, selectedValue];
            }
            else if (startDate && this.isSameDate(startDate, selectedValue) && endDate && this.isSameDate(endDate, selectedValue)) {
                intervalInnerValue = [];
            }
            else if (startDate && this.isSameDate(startDate, selectedValue)) {
                intervalInnerValue = [endDate, endDate];
            }
            else if (endDate && this.isSameDate(endDate, selectedValue)) {
                intervalInnerValue = [startDate, startDate];
            }
            else if (startDate && !endDate && selectedValue.isBefore(startDate)) {
                intervalInnerValue = [selectedValue, startDate];
            }
            else if (!startDate || selectedValue.isBefore(startDate)) {
                intervalInnerValue = [selectedValue, endDate];
            }
            else if (!endDate || selectedValue.isAfter(endDate) || selectedValue.isBetween(startDate, endDate)) {
                intervalInnerValue = [startDate, selectedValue];
            }
            else {
                intervalInnerValue = [startDate, startDate];
            }
            this.changeIntervalInnerValue(intervalInnerValue);
        };
        this.onTitleClick = () => {
            const { enabled = DatePickerProperties.enabled.defaultValue, loading = DatePickerProperties.loading.defaultValue, mode = DatePickerProperties.mode.defaultValue, showExpander = DatePickerProperties.showExpander.defaultValue, } = this.props;
            const { date, innerMode } = this.state;
            const isWeekMode = mode === DatePickerMode.WEEK;
            const isInnerWeekMode = innerMode === DatePickerMode.WEEK;
            const shouldSkipAction = isWeekMode || (isInnerWeekMode && showExpander);
            if (!enabled || loading || shouldSkipAction) {
                return;
            }
            const value = this.getValue();
            this.setState(({ innerMode }) => ({
                ...this.state,
                date: !Array.isArray(value) ? value : date,
                innerMode: incrementMode(innerMode),
            }));
        };
        this.onClear = () => {
            const { enabled = DatePickerProperties.enabled.defaultValue, interval = DatePickerProperties.interval.defaultValue, loading = DatePickerProperties.loading.defaultValue, } = this.props;
            if (!enabled || loading) {
                return;
            }
            if (interval) {
                this.changeIntervalInnerValue([]);
                return;
            }
            this.setState({
                date: null,
            }, () => {
                this.onApply(null);
            });
        };
        this.onToday = () => {
            const { enabled = DatePickerProperties.enabled.defaultValue, externalControls, loading = DatePickerProperties.loading.defaultValue, onNavigate, } = this.props;
            const { date } = this.state;
            if (!enabled || loading) {
                return;
            }
            const { interval = DatePickerProperties.interval.defaultValue, mode = DatePickerProperties.mode.defaultValue, showApply = DatePickerProperties.showApply.defaultValue, } = this.props;
            const today = moment().utc();
            const currentDate = moment(date).utc();
            const period = getTypeOfNavigabilityMode(mode);
            const dates = this.getDates(currentDate, today, period);
            const abort = createAbort();
            if (isFunction(onNavigate)) {
                onNavigate(new AbortableNavigateEvent({ abort, from: dates.initialDate, to: dates.finalDate }));
            }
            void abort.promise.then(aborted => {
                if (aborted) {
                    return;
                }
                this.setState({
                    date: today,
                    innerMode: mode,
                    intervalInnerValue: [today],
                }, () => {
                    if (interval && (externalControls || !showApply)) {
                        this.onApplyInterval();
                    }
                });
                if (!interval) {
                    this.onApply({ value: today });
                }
            });
        };
        this.onArrowClick = (value) => {
            const { enabled = DatePickerProperties.enabled.defaultValue, loading = DatePickerProperties.loading.defaultValue, onAfterNavigate, onNavigate, } = this.props;
            if (!enabled || loading) {
                return;
            }
            this.setState(previousState => {
                const { date, innerMode: mode } = previousState;
                const amount = mode === DatePickerMode.DECADES ? value * 10 : value;
                const period = getTypeOfNavigabilityMode(mode);
                const currentDate = moment(date).utc();
                const nextDate = moment(currentDate).utc().add(amount, period);
                const dates = this.getDates(currentDate, nextDate, period);
                const abort = createAbort();
                if (isFunction(onNavigate)) {
                    onNavigate(new AbortableNavigateEvent({ abort, from: dates.initialDate, to: dates.finalDate }));
                }
                void abort.promise.then(aborted => {
                    if (aborted) {
                        return;
                    }
                    if (isFunction(onAfterNavigate)) {
                        onAfterNavigate(new NavigateEvent({ from: dates.initialDate, to: dates.finalDate }));
                    }
                    this.setState({
                        amount,
                        date: currentDate.add(amount, period),
                        oldDate: date,
                        period,
                    });
                });
            });
        };
        this.toMomentDate = (date) => {
            return moment.parseZone(date).startOf('date').utc(true);
        };
        this.getDates = (currentDate, nextDate, period) => {
            const { amount } = this.state;
            const { interval = DatePickerProperties.interval.defaultValue } = this.props;
            const considerAmount = !isMobile() && interval;
            const absAmount = Math.abs(amount ?? 0);
            const getFinalDate = (date) => {
                const finalDate = considerAmount ? moment(date).utc().add(absAmount, period) : moment(date);
                return finalDate.endOf(period).toISOString();
            };
            const initialCurrentDate = moment(currentDate).startOf(period).toISOString();
            const finalCurrentDate = getFinalDate(currentDate);
            const initialNextDate = moment(nextDate).startOf(period).toISOString();
            const finalNextDate = getFinalDate(nextDate);
            return {
                finalDate: [initialNextDate, finalNextDate],
                initialDate: [initialCurrentDate, finalCurrentDate],
            };
        };
        this.onApplyInterval = () => {
            const { enabled = DatePickerProperties.enabled.defaultValue, loading = DatePickerProperties.loading.defaultValue, } = this.props;
            if (!enabled || loading) {
                return;
            }
            const { intervalInnerValue } = this.state;
            const { value: oldValue } = this.props;
            const value = intervalInnerValue.filter(Boolean).map(item => item.toISOString(true));
            this.triggerOnChange({ oldValue, value });
        };
        this.onApply = (date) => {
            const { value: oldValue } = this.props;
            const value = date && date.value?.toISOString(true) || '';
            this.triggerOnChange({ oldValue, value });
        };
        this.getValue = () => {
            const { interval = DatePickerProperties.interval.defaultValue, value, } = this.props;
            const { intervalInnerValue } = this.state;
            if (interval) {
                return intervalInnerValue;
            }
            if (!value) {
                return;
            }
            return Array.isArray(value) ? this.toMomentDate(value[0]) : this.toMomentDate(value);
        };
        this.handleChangeWeekMonthly = (isExpandedWeekMonthly) => {
            this.setState({
                innerMode: isExpandedWeekMonthly ? DatePickerMode.MONTHLY : DatePickerMode.WEEK,
                isExpandedWeekMonthly,
            });
        };
        this.handleChangeMode = (innerMode) => {
            this.setState({ innerMode });
        };
    }
    /**
     * Prettifies a given value.
     *
     * @param properties - Properties to generate pretty value.
     */
    static formatPrettyValue(properties) {
        const { value } = properties;
        if (!Array.isArray(value)) {
            return getPrettyDateValue(value);
        }
        const [startDate, endDate] = value;
        return getPrettyDateValue([startDate, endDate]);
    }
    changeIntervalInnerValue(intervalInnerValue) {
        const { externalControls, showApply = DatePickerProperties.showApply.defaultValue, } = this.props;
        this.setState({
            intervalInnerValue,
        }, () => {
            if (externalControls || !showApply) {
                this.onApplyInterval();
            }
        });
    }
    isSameMonthAndYear(dateA, dateB) {
        if (!dateA || !dateB) {
            return false;
        }
        const sameYear = dateA.year() === dateB.year();
        const sameMonth = dateA.month() === dateB.month();
        return sameYear && sameMonth;
    }
    isSameDate(dateA, dateB) {
        if (!dateA || !dateB) {
            return false;
        }
        return dateA.toISOString() === dateB.toISOString();
    }
    triggerOnChange({ oldValue, value }) {
        const { onChange } = this.props;
        if (isFunction(onChange)) {
            onChange(new ChangeEvent({
                oldValue,
                prettyValue: DatePicker.formatPrettyValue({ value }),
                value,
            }));
        }
    }
    componentDidMount() {
        const { interval = DatePickerProperties.interval.defaultValue, value, } = this.props;
        const date = Array.isArray(value) ? value[0] : value;
        const isValid = moment(date).isValid();
        if (date && isValid) {
            this.setState({ date: moment(date).utc() });
        }
        if (interval && value) {
            const intervalValue = Array.isArray(value)
                ? value
                : [value];
            this.setState({
                intervalInnerValue: intervalValue.map(this.toMomentDate),
            });
        }
    }
    componentDidUpdate(prevProps, prevState) {
        const { interval, value, initialDate, mode, weekMonthly } = this.props;
        const { value: prevValue, initialDate: prevInitialDate, mode: prevMode, weekMonthly: prevWeekMonthly } = prevProps;
        const { innerMode } = this.state;
        const { innerMode: prevInnerMode } = prevState;
        const date = Array.isArray(value) ? value[0] : value;
        const isValid = moment(date).isValid();
        if (isValid && value !== prevValue) {
            this.setState({ date: moment(date).utc() });
        }
        if (interval && value !== prevValue) {
            const intervalValue = Array.isArray(value)
                ? value
                : [value];
            this.setState({
                intervalInnerValue: intervalValue.map(this.toMomentDate),
            });
        }
        if (initialDate !== prevInitialDate) {
            this.setState({ date: moment(initialDate).utc() });
        }
        if (mode !== prevMode) {
            this.setState({
                innerMode: mode ?? DatePickerProperties.mode.defaultValue,
            });
        }
        const shouldUpdateIsExpandedWeekMonthly = (weekMonthly !== prevWeekMonthly) || (mode !== prevMode) || (innerMode !== prevInnerMode);
        if (shouldUpdateIsExpandedWeekMonthly) {
            const isExpandedWeekMonthly = mode !== DatePickerMode.WEEK || innerMode !== DatePickerMode.WEEK;
            this.setState({ isExpandedWeekMonthly });
        }
        if (!interval || !isValid || !Array.isArray(value)) {
            return;
        }
        const [prevStartDate, prevEndDate] = prevValue || [];
        const [startDate, endDate] = value || [];
        if (prevStartDate !== startDate || prevEndDate !== endDate) {
            this.setState({
                intervalInnerValue: value.map(this.toMomentDate),
            });
        }
    }
    shouldComponentUpdate(nextProps, nextState) {
        const stateChanged = shallowCompare(this.state, nextState, [
            'date',
            'innerMode',
            'isExpandedWeekMonthly',
            'intervalInnerValue',
        ]);
        if (stateChanged) {
            return true;
        }
        return shallowCompare(this.props, nextProps, [
            'data',
            'enabled',
            'initialDate',
            'interval',
            'intervalThreshold',
            'loading',
            'mode',
            'nativeOnMobile',
            'ranges',
            'rangeState',
            'showApply',
            'showClear',
            'showEvents',
            'showExpander',
            'showNavigationArrows',
            'value',
            'weekMonthly',
        ]);
    }
    getNativeType() {
        const { mode = DatePickerProperties.mode.defaultValue } = this.props;
        const nativeTypes = {
            [DatePickerMode.MONTHLY]: NativeDateType.DATE,
            [DatePickerMode.YEARLY]: NativeDateType.MONTH,
            [DatePickerMode.DECADES]: NativeDateType.DATE,
            [DatePickerMode.WEEK]: NativeDateType.DATE,
        };
        return nativeTypes[mode];
    }
    renderMobile() {
        const type = this.getNativeType();
        const value = this.getValue();
        return (React.createElement(NativeDate, { enabled: this.props.enabled ?? DatePickerProperties.enabled.defaultValue, onChange: (event) => this.onCellClick(event.value), type: type, value: moment.isMoment(value) ? value.toISOString(true) : '' }));
    }
    renderDatePicker() {
        const { data = DatePickerProperties.data.defaultValue, enabled = DatePickerProperties.enabled.defaultValue, externalControls, interval = DatePickerProperties.interval.defaultValue, intervalThreshold = DatePickerProperties.intervalThreshold.defaultValue, isModalWrapped = DatePickerProperties.isModalWrapped.defaultValue, loading = DatePickerProperties.loading.defaultValue, onCollapse, onNavigate, ranges = DatePickerProperties.ranges.defaultValue, rangeState = DatePickerProperties.rangeState.defaultValue, showApply = DatePickerProperties.showApply.defaultValue, showClear = DatePickerProperties.showClear.defaultValue, showEvents = DatePickerProperties.showEvents.defaultValue, showExpander = DatePickerProperties.showExpander.defaultValue, showNavigationArrows = DatePickerProperties.showNavigationArrows.defaultValue, weekMonthly = DatePickerProperties.weekMonthly.defaultValue, } = this.props;
        const { date, innerMode, isExpandedWeekMonthly } = this.state;
        const value = this.getValue();
        const momentDate = this.toMomentDate(date);
        const lostProps = getLostProps(DatePickerProperties, this.props);
        const optimizedRanges = optimizeRanges(ranges);
        const rangeEnabled = rangeState === RangeState.ENABLED;
        if (interval) {
            return (React.createElement(DatePickerInterval, { ...lostProps, date: momentDate, enabled: enabled, externalControls: externalControls, intervalThreshold: intervalThreshold, isModalWrapped: isModalWrapped, loading: loading, onArrowClick: this.onArrowClick, onCellClick: this.onIntervalCellClick, onClear: this.onClear, onClick: this.onApplyInterval, onToday: this.onToday, rangeEnabled: rangeEnabled, ranges: optimizedRanges, showApply: showApply, showClear: showClear, value: Array.isArray(value) ? value : undefined }));
        }
        return (React.createElement(DatePickerBasic, { ...lostProps, date: momentDate, enabled: enabled, events: data, externalControls: externalControls, handleChangeMode: this.handleChangeMode, isExpandedWeekMonthly: isExpandedWeekMonthly, isModalWrapped: isModalWrapped, loading: loading, mode: innerMode, onArrowClick: this.onArrowClick, onCellClick: this.onCellClick, onChangeWeekMonthly: this.handleChangeWeekMonthly, onClear: this.onClear, onCollapse: onCollapse, onNavigate: onNavigate, onTitleClick: this.onTitleClick, onToday: this.onToday, rangeEnabled: rangeEnabled, ranges: optimizedRanges, showClear: showClear, showEvents: showEvents, showExpander: showExpander, showNavigationArrows: showNavigationArrows, value: !Array.isArray(value) ? value : undefined, weekMonthly: weekMonthly }));
    }
    render() {
        const { nativeOnMobile = DatePickerProperties.nativeOnMobile.defaultValue, } = this.props;
        const isNative = isMobile() && nativeOnMobile;
        return isNative
            ? this.renderMobile()
            : this.renderDatePicker();
    }
}
export { DatePicker, };
//# sourceMappingURL=date-picker.js.map